import React, { ReactElement, useState } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { PortAndWaterwayObjectReport } from '../../../../types';
import { useSelector } from 'react-redux';
import { MapState } from '../../map/reducer';
import { post } from '../../../../utils';

const reportSchema = Yup.object().shape({
  title: Yup.string().min(4, 'Ämne är för kort').max(75, 'Ämne är för långt').required('Ämne är obligatoriskt'),
  description: Yup.string().min(4, 'Meddelande är för kort').max(5000, 'Meddelande är för långt').required('Meddelande är obligatoriskt'),
  reporterName: Yup.string().min(1, 'Namn är för kort').max(75, 'Namn är för långt').required('Namn är obligatoriskt'),
  reporterPhone: Yup.string().min(9, 'Telefonnummer är för kort').max(75, 'Telefonnummer är för långt').required('Telefonnummer är obligatoriskt'),
  reporterEmail: Yup.string().email('Felaktig e-post').required('E-post är obligatoriskt'),
  recaptcha: Yup.string().required('Du måste svara på captcha'),
});

interface SubmitReportProps {
  id: number;
}

export default function SubmitReport({ id }: SubmitReportProps): ReactElement {
  const [show, setShow] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const recaptchaRef = React.useRef(undefined);
  const { recaptchaSiteKey } = useSelector((state: { map: MapState }) => ({
    recaptchaSiteKey: state.map.configuration.recaptchaSiteKey,
  }));
  console.log('recaptchaSiteKey: ', recaptchaSiteKey);
  return (
    <div id="sidebar-first" className="sidebar sticky">
      <aside className="sidebar-first" role="complementary">
        <div className="region region-sidebar-first">
          <div className={`contextual-region block hb-container ${show ? 'open' : 'closed'}`}>
            <div className="block-title" onClick={() => setShow(!show)}>
              <h3>
                <span className="disable-select">Rapportera iakttagelse</span>
                <div>
                  <div className="hb-toggle">
                    <div className="hb-toggle-icon" />
                  </div>
                </div>
              </h3>
            </div>

            <div className="block-content hb-item-content">
              {isSubmitted && (
                <div>
                  <div className="message-box">
                    <div className="message-icon icon-important" />
                    <div className="message-text">Rapport skickad</div>
                  </div>
                </div>
              )}
              {!isSubmitted && (
                <Formik
                  initialValues={{ id, title: '', description: '', reporterName: '', reporterEmail: '', reporterPhone: '', recaptcha: '' }}
                  validationSchema={reportSchema}
                  onSubmit={(values, actions) => {
                    console.log(values);
                    actions.setSubmitting(true);
                    post(`/api/sxk-map/submit-report`, JSON.stringify(values))
                      .then(() => {
                        actions.resetForm();
                        setSubmitted(true);
                      })
                      .catch((e) => {
                        console.error(e);
                      })
                      .finally(() => {
                        actions.setSubmitting(false);
                      });
                  }}
                >
                  {(props: FormikProps<PortAndWaterwayObjectReport>) => (
                    <form className="contextual-region" onSubmit={props.handleSubmit} method="post" acceptCharset="UTF-8">
                      <div className="form-wrapper grouped-items">
                        <Field name="reporterName">
                          {({ field, form, meta }) => (
                            <div className="form-type-textfield form-item-name">
                              <label htmlFor="reporterName" className="form-required">
                                Namn
                              </label>
                              <input
                                {...field}
                                type="text"
                                id="reporterName"
                                size={60}
                                maxLength={75}
                                className="form-text required"
                                required
                                aria-required="true"
                              />
                              {meta.touched && meta.error && <span className="form-field-invalid">{meta.error}</span>}
                              <span>Minst 1 tecken, max 75 tecken</span>
                            </div>
                          )}
                        </Field>
                        <Field name="reporterEmail">
                          {({ field, form, meta }) => (
                            <div className="form-item form-type-email form-item-email">
                              <label htmlFor="reporterEmail" className="form-required">
                                E-post
                              </label>
                              <input
                                {...field}
                                type="email"
                                id="reporterEmail"
                                size={60}
                                maxLength={75}
                                className="form-email required"
                                required
                                aria-required="true"
                              />
                              {meta.touched && meta.error && <span className="form-field-invalid">{meta.error}</span>}
                            </div>
                          )}
                        </Field>
                        <Field name="reporterPhone">
                          {({ field, form, meta }) => (
                            <div className="webform-elements form-wrapper grouped-items">
                              <div className="form-item form-type-textfield form-item-name">
                                <label htmlFor="reporterPhone" className="form-required">
                                  Telefon
                                </label>
                                <input
                                  {...field}
                                  type="text"
                                  id="reporterPhone"
                                  size={60}
                                  maxLength={75}
                                  className="form-text required"
                                  required
                                  aria-required="true"
                                />
                                {meta.touched && meta.error && <span className="form-field-invalid">{meta.error}</span>}
                                <span>Minst 9 tecken, max 75 tecken</span>
                              </div>
                            </div>
                          )}
                        </Field>
                        <Field name="title">
                          {({ field, form, meta }) => (
                            <div className="form-item form-type-textfield form-item-subject">
                              <label htmlFor="title" className="form-required">
                                Ämne
                              </label>
                              <input
                                {...field}
                                type="text"
                                id="title"
                                size={60}
                                maxLength={75}
                                className="form-text required"
                                required
                                aria-required="true"
                              />
                              {meta.touched && meta.error && <span className="form-field-invalid">{meta.error}</span>}
                              <span>Minst 4 tecken, max 75 tecken</span>
                            </div>
                          )}
                        </Field>
                        <Field name="description">
                          {({ field, form, meta }) => (
                            <div className="form-item form-type-textarea form-item-message">
                              <label htmlFor="description" className="form-required">
                                Meddelande
                              </label>
                              <div className="form-textarea-wrapper">
                                <textarea
                                  {...field}
                                  id="description"
                                  rows={5}
                                  cols={60}
                                  className="form-textarea required resize-vertical"
                                  required
                                  aria-required="true"
                                />
                                {meta.touched && meta.error && <span className="form-field-invalid">{meta.error}</span>}
                                <span>Minst 4 tecken, max 5000 tecken</span>
                              </div>
                            </div>
                          )}
                        </Field>
                        {recaptchaSiteKey && (
                          <div className="form-item form-type-textfield form-item-subject">
                            <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey} language="sv">
                              <GoogleReCaptcha
                                onVerify={(response) => {
                                  props.setFieldValue('recaptcha', response);
                                }}
                              />
                            </GoogleReCaptchaProvider>
                          </div>
                        )}
                        <div className="form-actions webform-actions form-wrapper grouped-items">
                          <input
                            className="button button--primary form-submit"
                            type="submit"
                            value="Skicka"
                            disabled={props.isSubmitting || !props.isValid}
                          />
                        </div>
                      </div>
                    </form>
                  )}
                </Formik>
              )}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
